import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from "recharts";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import {
  getClickedTMs,
  renderMarkers,
  ChartBandZoomButton,
} from "../../../common/chartZoom";

type Point = { timestamp: number; value: number | null };

type Props = {
  data: Point[];
  title?: string;
  // [start, end] in the data's timestamp space (Nashville-naive ms); pins the axis.
  domain: [number, number] | null;
  markers?: number[];
  onChartClick?: (t: number) => void;
  onZoom?: () => void;
};

// Timestamps are Nashville-naive (parsed without tz conversion), so browser-local
// formatting renders the Nashville wall-clock identically on any browser.
const renderTickDesktop = (props: any): React.ReactElement<SVGElement> => {
  const { x, y, payload } = props;
  const label = format(new Date(payload.value), "MMM d, HH:mm");
  return (
    <text
      x={x}
      y={y + 10}
      transform={`rotate(-45, ${x}, ${y})`}
      textAnchor="end"
      fill="#666"
      fontSize={12}
    >
      {label}
    </text>
  );
};

const renderTickMobile = (props: any): React.ReactElement<SVGElement> => {
  const { x, y, payload } = props;
  const label = format(new Date(payload.value), "MMM d");
  return (
    <text x={x} y={y + 12} textAnchor="middle" fill="#666" fontSize={11}>
      {label}
    </text>
  );
};

const MotorCurrentChart: React.FC<Props> = ({
  data,
  title,
  domain,
  markers = [],
  onChartClick,
  onZoom,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const display = useMemo(() => {
    const arr = (data || []).filter(
      (d) => d.value != null && Number.isFinite(d.timestamp),
    );
    arr.sort((a, b) => a.timestamp - b.timestamp);
    return arr;
  }, [data]);

  const handleClick = (state: any) => {
    if (!onChartClick) return;
    const t = getClickedTMs(state);
    if (t != null) onChartClick(t);
  };

  const height = isMobile ? 260 : 340;
  const margin = isMobile
    ? { top: 6, right: 12, bottom: 28, left: 8 }
    : { top: 5, right: 60, bottom: 60, left: 60 };
  const xTick = isMobile ? renderTickMobile : renderTickDesktop;

  // Force re-apply of a changed domain (Recharts can otherwise blank-render).
  const chartKey = domain ? `${domain[0]}-${domain[1]}` : "full";

  return (
    <>
      <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 1 }}>
        {title ?? "Extruder Motor Current (A)"}
      </Typography>
      <Box sx={{ width: "100%", minWidth: 280, height, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={display}
            key={chartKey}
            margin={margin}
            onClick={handleClick}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={domain ?? (["auto", "auto"] as any)}
              scale="time"
              tick={xTick}
              interval="preserveEnd"
              tickMargin={isMobile ? 8 : 12}
              allowDataOverflow
              height={isMobile ? 26 : 60}
            />
            <YAxis
              tickMargin={isMobile ? 6 : 8}
              allowDataOverflow
              label={
                isMobile
                  ? undefined
                  : {
                      value: "Current (A)",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      style: { textAnchor: "middle" },
                    }
              }
            />
            <Tooltip
              isAnimationActive={false}
              formatter={(v: any) => [`${v} A`, "Motor Current"]}
              labelFormatter={(ts: number) => format(new Date(ts), "MMM d, HH:mm")}
            />
            <Line
              type="linear"
              dataKey="value"
              name="Current (A)"
              dot={false}
              stroke="#1E90FF"
              strokeWidth={2}
              isAnimationActive={false}
            />
            {renderMarkers(markers)}
          </LineChart>
        </ResponsiveContainer>
        <ChartBandZoomButton
          domain={domain}
          markers={markers}
          onZoom={onZoom}
          leftMargin={margin.left}
          rightMargin={margin.right}
        />
      </Box>
    </>
  );
};

export default MotorCurrentChart;
